import { useState, useRef, useEffect, useCallback } from 'react';
import { ReactComponent as CloseIcon } from "../../../../../Assets/icon/close.svg";
import { ReactComponent as PlusIcon } from "../../../../../Assets/icon/plus-gray.svg";
import { ReactComponent as CheckIcon } from "../../../../../Assets/icon/Check-white.svg";
import searchIcon from "../../../../../Assets/images/searchIcon.png"
import { getRelationships } from "../../../../../Services/Realtionship"
import "./style.css"
import ContentLoader from '../../../../LoadingPage/ContentLoader';
import { checkDuration, checkUserActivation, checkUsersActivationBatch } from '../../../../../Services/coaching';

const GroupMemberSelectionModal = ({onClose, hideCloseIcon, coupleCoaching}) => {

  const [activeFilter, setActiveFilter] = useState('all');
  const [gridLoading, setGridLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [activationMap, setActivationMap] = useState({}); // { userId: boolean }
  const [searchTerm, setSearchTerm] = useState('');
  const isFetching = useRef(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [error,setError]= useState(null);
  const [loading, setLoading] = useState(false);
  // Only show filter chips for categories that actually exist
  const [availableFilters, setAvailableFilters] = useState([]); // lowercase category keys, excludes 'all'
  const profile = JSON.parse(localStorage.getItem("profile"));
  const userId = profile._id;

  const fetchRelationships = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;

    setGridLoading(true);
    try {
      const relationshipsData = await getRelationships(
        activeFilter === "all" ? "" : activeFilter
      );
      // Show ONLY accepted + linked contacts
      const allowed = (relationshipsData || []).filter(
        (c) => c?.status === 'accepted' && !!c?.related_user_id
      );
      setContacts(allowed);
      setFilteredContacts(allowed);

      // Derive available category filters from the response
      const presentCats = Array.from(
        new Set(
          (allowed || [])
            .map((c) => (c?.relationship_category || '').toLowerCase())
            .filter((c) => !!c && c !== 'all')
        )
      );

      // Maintain a stable, human-friendly order
      const knownOrder = [
        'dating',
        'marriage',
        'family',
        'friends',
        'workplace',
        'roommates',
        'sports',
        'others',
        'other',
      ];
      const presentSet = new Set(presentCats);
      const orderedKnown = knownOrder.filter((k) => presentSet.has(k));
      const unknownCats = presentCats.filter((c) => !knownOrder.includes(c)).sort();
      const nextAvailable = [...orderedKnown, ...unknownCats];

      // Update available filters if changed
      if (JSON.stringify(nextAvailable) !== JSON.stringify(availableFilters)) {
        setAvailableFilters(nextAvailable);
        // If current active filter is no longer present, reset to 'all'
        if (activeFilter !== 'all' && !nextAvailable.includes(activeFilter)) {
          setActiveFilter('all');
        }
      }

      // Prepare batch activation check for allowed contacts only (kept for future use, not required for filtering)
      const eligibleUserIds = allowed.map(c => c.related_user_id);

      if (eligibleUserIds.length > 0) {
        const map = await checkUsersActivationBatch(eligibleUserIds);
        setActivationMap(map || {});
      } else {
        setActivationMap({});
      }
    } catch (error) {
      console.error("Failed to fetch relationships:", error);
      setContacts([]);
      setFilteredContacts([]);
      setActivationMap({});
    } finally {
      isFetching.current = false;
      setGridLoading(false);
    }
  }, [activeFilter, availableFilters]);

  useEffect(() => {
    fetchRelationships();
  },[fetchRelationships]);

  // Filter contacts based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredContacts(contacts);
      return;
    }
    
    const filtered = contacts.filter(contact => {
      const fullName = `${contact.first_name} ${contact.last_name}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase());
    });
    
    setFilteredContacts(filtered);
  }, [searchTerm, contacts]);

  const handleSelectContact = (contact) => {
    const contactInfo = {
      id: contact._id,
      email: contact?.contact?.email || '',
      firstName: contact.first_name,
      lastName: contact.last_name,
      // New API: partner user id is provided as `related_user_id`
      user_id: contact?.related_user_id || null,
    };
    
    if (coupleCoaching) {
      // Single selection for couple coaching
      if (selectedContacts.length > 0 && selectedContacts[0].id === contact._id) {
        setSelectedContacts([]);
      } else {
        setSelectedContacts([contactInfo]);
      }
    } else {
      // Multiple selection for group coaching
      const existingIndex = selectedContacts.findIndex(c => c.id === contact._id);
      
      if (existingIndex >= 0) {
        // Remove the contact if already selected
        const updatedContacts = [...selectedContacts];
        updatedContacts.splice(existingIndex, 1);
        setSelectedContacts(updatedContacts);
      } else {
        // Add the contact to selected contacts
        setSelectedContacts([...selectedContacts, contactInfo]);
      }
    }
  };

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return '';
    if (!firstName) return lastName[0]?.toUpperCase() || '';
    if (!lastName) return firstName[0]?.toUpperCase() || '';
    return `${firstName[0]?.toUpperCase()}${lastName[0]?.toUpperCase()}`;
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleNavigation = async () => {
    setError(null)
    setLoading(true);
    try {
      // Use the new mock API to check user activation status
      const response = await checkUserActivation()
      if(response.activation === false) {
        // For activation flow, initiate single person dialog session
        window.location.href = '/coach/private?activation=true'
        return;
      } else {
        // Redirect to the enhanced ArisCoach component with the selected contact IDs
        if (selectedContacts.length > 0) {
          // Extract user IDs and join them with commas for the URL
          const contactIds = selectedContacts.map(contact => contact.user_id).join(',');
          const relationIds = selectedContacts.map(contact => contact.id).join(',');
          // Use the updated Coach/index.jsx component which now handles both private and group coaching
          window.location.href = `/group-coach?user_ids=${contactIds}&relation_ids=${relationIds}`
        } else {
          window.location.href = '/dialogues'
        }
      }
    } catch (error) {
      console.error("Error checking user activation status:", error);
      setError("Server error. Please try again later")
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className='coaching-add-member-container'>
      <div className="coaching-add-member-header">
        <p>Select your session partner</p>
        {!hideCloseIcon && <CloseIcon style={{cursor:'pointer'}} onClick={onClose}/>}
      </div>
      <div className="coaching-add-member-header-sub-heading">
        <p>You can only select Aris members from your relationships with whom you have shared your Aris elements.</p>
      </div>
      {gridLoading ? 
        <div className='group-coach-grid-loading'>
          <ContentLoader /> 
        </div>
      : 
        <>
          <div className="coaching-add-member-search-bar">
            <span><img src={searchIcon} alt='search' /></span>
            <input
              type="text" 
              placeholder='Search by name'
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div className="coaching-add-member-filter-chips-container">
            {['all', ...availableFilters].map((filter) => (
              <div
                key={filter}
                className={`chip-item ${activeFilter === filter && 'active'}`}
                onClick={() => setActiveFilter(filter)}
              >
                 {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </div>
            ))}
          </div>
          <div className="coaching-add-member-profiles-container">
            {filteredContacts.length > 0 ? (
              filteredContacts.map((contact) => {
                const isSelected = selectedContacts.some(c => c.id === contact._id);
                // Only selection-mode disabling applies now (list already pruned)
                const selectionModeDisabled = coupleCoaching && selectedContacts.length > 0 && !isSelected;
                const isDisabled = selectionModeDisabled;
                
                return (
                  <div key={contact._id} className="coaching-add-member-profile-item">
                    <div className="coaching-add-member-profile-item-left">
                      <div className="coaching-add-member-profile-item-image">
                        {contact.profile_pic_url ? (
                          <img src={contact.profile_pic_url} alt={getInitials(contact.first_name, contact.last_name)} />
                        ) : (
                          <div className="initials">
                            {getInitials(contact.first_name, contact.last_name)}
                          </div>
                        )}
                      </div>
                      <div className="coaching-add-member-profile-item-details">
                        <p className='coaching-add-member-profile-item-name'>{contact.first_name + " " + contact.last_name}</p>
                        <p className='coaching-add-member-profile-item-relationship'>{contact.relationship_category}</p>
                      </div>
                    </div>
                    <div className="coaching-add-member-profile-item-right">
                      <div 
                        className={`coaching-add-member-add-button-conatiner 
                          ${isSelected ? 'added' : ''} 
                          ${isDisabled ? 'disabled' : ''}`} 
                        onClick={() => !isDisabled && handleSelectContact(contact)}
                        style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.5 : 1 }}
                      >
                        {isSelected ? (
                          <>
                            Added
                            <CheckIcon />
                          </>
                        ) : (
                          <>
                            Add
                            <PlusIcon />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-results text-center">No Aris members available to select</div>
            )}
          </div>
          {error && <div className="error-message" style={{textAlign: 'center'}}>{error}</div>}
          <div className="coaching-add-member-footer">
            {selectedContacts.length > 0 &&
                <div 
                    className="coaching-add-member-footer-button-conatiner" 
                    onClick={() => {
                        handleNavigation();
                    }}
                >
                    {loading 
                      ? 'Starting...' 
                      : coupleCoaching 
                        ? `Start dialog with ${selectedContacts[0].firstName}`
                        : `Start dialogue with ${selectedContacts.length} ${selectedContacts.length === 1 ? 'contact' : 'contacts'}`
                    }
                </div>
            }
          </div>
        </>}
    </div>
  )
}

export default GroupMemberSelectionModal;